import { TaxReturnSubType, TaxReturnType } from "../../../swaggerServices";

export class SubtypeChain {
  constructor(
    public chain: TaxReturnSubType[],
    public isLongest?: boolean,
  ) {
  }
  
  get firstSubtype() {
    return this.chain[0];
  }
  
  get length() {
    return this.chain.length;
  }
}

// Convenience wrapper around type-subtype relationships
export class TypeSubtypeLookup {
  constructor(
    public types: Map<string, TaxReturnType>,
    public subtypes: Map<string, TaxReturnSubType>,
    public chains: Map<string, TaxReturnSubType[]>,
  ) {
  }

  private getActiveSubtypes(): TaxReturnSubType[] {
    return Array.from(this.subtypes.values()).filter(st => !st.status);
  }

  private getActiveTypes(): TaxReturnType[] {
    return Array.from(this.types.values()).filter(t => !t.status);
  }

  getAvailableSubtypesForTypeId(taxReturnTypeId: number): TaxReturnSubType[] {
    const result = this.getActiveSubtypes().filter(st => st.taxReturnTypeId === taxReturnTypeId);
    return result;
  }

  // If a type is active AND has a active subtypes then it is available 
  getAvailableTypes(): TaxReturnType[] {
    const typeIdsOfSubtypes = this.getActiveSubtypes().map(t => t.taxReturnTypeId);
    const typesWithSubtypes: Set<number> = new Set(typeIdsOfSubtypes);
    const result = this.getActiveTypes().filter(t => typesWithSubtypes.has(t.id));
    return result;
  }
}

export function typeToDropdownListOptions(trt: TaxReturnType, order: number) {
  return {
    id: trt.id,
    name: trt.type,
    value: trt.id.toString(),
    order,
  };
}

export function subtypeToDropdownListOptions(subtype: TaxReturnSubType, order: number) {
  return {
    id: subtype.id,
    name: subtype.subType,
    value: subtype.id.toString(),
    order,
  };
}

export function subtypeChainToDropdownListOption(subtypeChain: SubtypeChain, order: number) {
  const chainOfIds = subtypeChain.chain.map(s => s.id);
  const label = `Latest from ${subtypeChain.chain.map(s => s.subType).join(' → ')}`;
  const value = `${chainOfIds.join(',')}_c`;
  return {
    id: order,
    name: label,
    value,
    order,
  }
}

// Given taxReturnSubtypes, finds and annotates all chains
export function generateSubtypeChains(taxReturnSubtypes: TaxReturnSubType[]): SubtypeChain[] {
  const subtypeMap: Map<string, TaxReturnType> = new Map();
  taxReturnSubtypes.forEach((trst: TaxReturnSubType) => {
    subtypeMap.set(trst.id.toString(), trst);
  });

  const chains: { [trstId: string]: number[] } = {};
  const isLong: { [trstId: string]: boolean } = {};
  taxReturnSubtypes.forEach((trst: TaxReturnSubType) => {
    generateChainsRecurse(trst, chains, isLong, subtypeMap);
  });

  const result = [];
  Object.keys(chains).forEach(subtypeId => {
    const theChain = chains[subtypeId].map((id) => (subtypeMap.get(id.toString())));
    const subtypeChain = new SubtypeChain(theChain, isLong[subtypeId]);
    result.push(subtypeChain);
  });

  return result;
}

// Updates chains and isLong inputs for memorization
function generateChainsRecurse(
  subtype: TaxReturnSubType,
  chains: { [trstId: string]: number[] },
  isLong: { [trstId: string]: boolean },
  subtypeMap: Map<string, TaxReturnType>,
): number[] {
  if (!subtype) { return []; }

  if (chains[subtype.id]) {
    isLong[subtype.id] = false;
    return chains[subtype.id];
  }

  let chainOfIds = [subtype.id];
  if (subtype.priorSubTypeId) {
    isLong[subtype.id] = true;
    const prior = subtypeMap.get(subtype.priorSubTypeId.toString());
    const subchain = generateChainsRecurse(prior, chains, isLong, subtypeMap);
    chainOfIds = chainOfIds.concat(subchain);
  }
  chains[subtype.id] = chainOfIds;

  return chains[subtype.id];
}
